import {
  Component,
  computed,
  DestroyRef,
  ErrorHandler,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { AnalyticsService } from '../../../../../analytics';
import {
  COPIED_FEEDBACK_MS,
  ERROR_FEEDBACK_MS,
  TooltipFeedback,
} from './action-feedback';

/**
 * The Table widget's copy action. Decisions cited below as `KD-n` resolve in
 * SB-3514's Technical Plan,
 * `.ai/plans/SB-3514_table-rendering-in-chat/SB-3514-technical-plan.md`.
 *
 * Projected into `<sd-response-table>`'s title-bar slot rather than living
 * inside the widget: the widget is a `serious-system` primitive and cannot
 * depend on the app, and this button reports to app-level analytics.
 *
 * The clipboard shape — one `ClipboardItem` carrying `text/html` and
 * `text/plain` — is the one already shipped at
 * `quick-actions/message-quick-actions-copy.component.ts:112-127` (KD-4).
 * ⚠️ Only the *shape* is inherited. That component
 * is sanitized incidentally, by passing its content through `MarkdownPipe` and
 * `DomSanitizer`; the payloads here are hand-built, so escaping (KD-17) and
 * formula neutralization (KD-18) are explicit and live in
 * `response-table.clipboard.ts` where they are testable without a DOM.
 *
 * Both payloads carry the **whole** model — every row and column, past both
 * render caps — because the caps are applied at the widget's render loop and
 * never to the model itself (KD-11).
 */
@Component({
  selector: 'squadbox-response-table-copy',
  imports: [
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
    MatTooltipModule,
    TranslateModule,
  ],
  template: `
    <!-- The aria-label is the control's *name* and stays "Copy table" while the
         tooltip is showing the transient "Copied!" feedback. An icon-only button
         has no other accessible name; the shipped message-copy action has none
         at all, which is not a precedent worth inheriting. -->
    <button
      type="button"
      sdIconButton
      variant="icon"
      size="xs"
      [color]="hasError() ? 'warning' : 'neutral'"
      [attr.aria-label]="'CHAT.TABLE.COPY' | translate"
      [matTooltip]="tooltipKey() | translate"
      matTooltipPosition="below"
      (click)="onCopy()"
    >
      <!-- The glyph swaps to a checkmark for the feedback window, which the
           spec asks for and the first cut never implemented (#1198). It rides
           the same signal as the tooltip so the two can never disagree. -->
      <svg [sdUseUIBasicIcon]="hasCopied() ? 'check' : 'copy'"></svg>
    </button>

    <!-- Both outcomes have to reach a screen reader. The tooltip is shown
         programmatically, which announces nothing, and the aria-label stays
         "Copy table" because it is the control's name rather than its state —
         so on the failure path a blind user would otherwise get no signal at
         all, and on the success path only the sighted checkmark. Empty at rest,
         so each outcome is a change the live announcer picks up. -->
    <span class="sr-only" role="status">
      @if (!isIdle()) {
        {{ tooltipKey() | translate }}
      }
    </span>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class ResponseTableCopyComponent {
  public readonly table = input.required<fromSeriousSystem.SdResponseTable>();

  private readonly tooltip = viewChild.required(MatTooltip);

  /**
   * `table_copied` is fired from here rather than surfaced as an `@Output` for
   * a host to wire.
   *
   * This component *is* the app layer — it already injects `ErrorHandler` — so
   * there is no boundary an output would be protecting. What an output does buy
   * is a way to forget: the only host is the component that composes a
   * message's segments, so a `copied` output would ship with no listener and no
   * failing test. `message_copied` fires from inside the message-copy button
   * for the same reason.
   */
  private readonly analyticsService = inject(AnalyticsService);

  /** `app.config.ts` provides `Sentry.createErrorHandler()` for this token. */
  private readonly errorHandler = inject(ErrorHandler);

  constructor() {
    inject(DestroyRef).onDestroy(() => this.feedback.destroy());
  }

  /**
   * The translation *key*, resolved by the pipe in the template rather than by
   * `TranslateService.instant()`. `instant()` is not a signal read, so a switch
   * of language would leave this button holding the boot-time string for the
   * life of the session.
   */
  protected readonly tooltipKey = signal('CHAT.TABLE.COPY');

  /** The shared feedback window (`action-feedback.ts`), keyed on `tooltipKey`. */
  private readonly feedback = new TooltipFeedback(
    this.tooltipKey,
    'CHAT.TABLE.COPY',
    () => this.tooltip(),
  );

  /**
   * Whether the button is inside its "Copied!" feedback window.
   *
   * Derived from `tooltipKey` rather than kept as a second signal on purpose:
   * the spec pairs the checkmark and the tooltip as one state, so a separate
   * flag would be two things to reset and one of them would eventually be
   * missed. The `aria-label` deliberately does *not* follow — it is the
   * control's name, not its state.
   */
  protected readonly hasCopied = computed(
    () => this.tooltipKey() === 'CHAT.COPIED',
  );

  /** Drives the warning color, the way the save action's `hasError` does. */
  protected readonly hasError = computed(
    () => this.tooltipKey() === 'CHAT.TABLE.COPY_ERROR',
  );

  /** At rest, so the live region stays empty until there is something to say. */
  protected readonly isIdle = computed(
    () => this.tooltipKey() === 'CHAT.TABLE.COPY',
  );

  public async onCopy(): Promise<void> {
    const table = this.table();

    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([fromSeriousSystem.buildHtmlTable(table)], {
            type: 'text/html',
          }),
          'text/plain': new Blob(
            [fromSeriousSystem.serializeMarkdownTable(table)],
            { type: 'text/plain' },
          ),
        }),
      ]);
    } catch (error) {
      // The shipped message-copy action shows "Copied!" even when the write
      // never happened — its feedback sits outside the guard. Do not inherit
      // that: a rejected write reports, and says so on screen.
      this.errorHandler.handleError(error);
      this.showFeedback('CHAT.TABLE.COPY_ERROR', ERROR_FEEDBACK_MS);
      return;
    }

    this.showTooltipCopied();
    this.analyticsService
      .sendTableCopiedEvent(fromSeriousSystem.tableActionMetrics(table))
      .subscribe();
  }

  private showTooltipCopied(): void {
    this.showFeedback('CHAT.COPIED', COPIED_FEEDBACK_MS);
  }

  private showFeedback(key: string, durationMs: number): void {
    this.feedback.show(key, durationMs);
  }
}
